import NoDataCard from '@/components/NoDataCard';
import { type TreatmentValidationIssue } from '@/components/Studies/Create/Steps/GroupsAndTreatments/GroupsAndTreatment.utils';
import { classificationTypes, handleIconClick } from '@/components/Studies/Treatments/Treatment.utils';
import Icon from '@/components/UI/Icon';
import Indicator from '@/components/UI/Indicator';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { fetchMetadataColumns, formatNumber } from '@/helpers';
import { _isEmpty, _isEmptyString, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { MetadataField } from '@/model/Metadata.model';
import type { ClassificationType, Treatment, TreatmentField } from '@/model/Treatment.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { startCase } from 'lodash';
import { type FC, useMemo } from 'react';

const valueWithUnit = (value?: TreatmentField) => {
  if (_isNil(value) || _isNil(value?.default_value)) {
    return '-';
  }
  return `${formatNumber(value?.default_value, true, 3)} ${value?.unit.name ?? ''}`.trim();
};

interface TreatmentsTableProps {
  treatments: Array<Treatment>;
  metadata?: Array<MetadataField>;
  groups: Array<TreatmentGroup>;
  handleAddOrUpdateTreatmentsClick: (_: unknown, treatment?: Treatment) => void;
  selectedRowsChange: (selectedRows: Record<number, boolean>) => void;
  classificationType: ClassificationType;
  selectedRows: Record<number, boolean>;
  treatmentIssues?: Map<string, TreatmentValidationIssue>;
}

export const TreatmentsTable: FC<TreatmentsTableProps> = ({
  treatments,
  metadata,
  groups,
  handleAddOrUpdateTreatmentsClick,
  selectedRowsChange,
  classificationType,
  selectedRows,
  treatmentIssues,
}) => {
  const defaultColumns: Array<TanStackColumnDef<Treatment>> = useMemo(
    () => [
      {
        id: 'label',
        header: 'Label',
        size: 300,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: Treatment } }) => {
          const hasIssue = _notNil(treatmentIssues) && treatmentIssues.has(original.temp_id ?? '');
          return (
            <div className="flex items-center pointer">
              {hasIssue && (
                <Indicator smallIndicator tooltip="Treatment has invalid metadata options" indicatorClassName="mr2" />
              )}
              <a className="link" onClick={() => handleAddOrUpdateTreatmentsClick('', original)}>
                {_isNil(original?.treatment_label) ? original?.treatment_type.name : original?.treatment_label}{' '}
                {(original?._duplicate ?? false) && <span className="ml2 dark-gray">(duplicate)</span>}
              </a>
            </div>
          );
        },
      },
      {
        id: 'name',
        header: 'Name',
        size: 300,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: Treatment } }) => (
          <div className="flex items-center pointer">
            {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
            {original.treatment_type?.benchling_link && (
              <Icon
                className="blue dib mr1"
                onClick={() => {
                  handleIconClick(original);
                }}
                style={{ minWidth: '40px', paddingTop: '3px' }}
                tooltip="Click to view in new tab"
                icon="jeffy_p_circle"
                width="24"
                height="24"
                viewBox="0 0 20 20"
              />
            )}
            <span>{original?.treatment_type?.name ?? '-'} </span>
          </div>
        ),
      },
    ],
    [handleAddOrUpdateTreatmentsClick, treatmentIssues]
  );

  const groupsColumn = useMemo(
    () => ({
      id: 'groups',
      header: 'Groups',
      accessorFn: (treatment: Treatment) => treatment?.study_groups?.map((group) => group.name)?.join(', ') ?? '-',
      enableSorting: true,
    }),
    []
  );

  const additionalColumns = useMemo(() => {
    const newAdditionalColumnMap = new Map(
      treatments.flatMap((treatment) =>
        treatment.fields.map((field) => [
          field.name,
          {
            id: field.name,
            header: startCase(field.name),
            accessorFn: (row: Treatment) => valueWithUnit(row.fields.find((x) => x.name === field.name)),
            enableSorting: true,
          },
        ])
      )
    );
    return [...newAdditionalColumnMap.keys()]?.map((columnId) => newAdditionalColumnMap.get(columnId));
  }, [treatments]);

  const metaColumns = useMemo(
    () => (_isNotEmpty(metadata) ? fetchMetadataColumns({ metadata, showAllColumns: true }) : []),
    [metadata]
  );

  const columns = useMemo(
    () =>
      [...defaultColumns, ...additionalColumns, groupsColumn, ...metaColumns] as Array<TanStackColumnDef<Treatment>>,
    [defaultColumns, additionalColumns, groupsColumn, metaColumns]
  );

  const classificationPlural = classificationTypes[classificationType].plural;
  const classificationKey = classificationTypes[classificationType].key;
  const hasNoGroupsAndNotDisease = _isEmpty(groups) && classificationType != 'disease_induction';

  return _isEmpty(treatments) ? (
    <NoDataCard
      title={`This study has no ${classificationPlural}`}
      text={`${hasNoGroupsAndNotDisease ? 'Please add at least one group to enable adding treatments' : `A preview of your ${classificationPlural} will appear here`}`}
      onLinkClick={!hasNoGroupsAndNotDisease ? handleAddOrUpdateTreatmentsClick : undefined}
      btnTxt={`Add a ${classificationKey}`}
      openModal={true}
      dark
      className="w-100 pa4"
    />
  ) : (
    <Table
      className="w-100 bt"
      columns={columns}
      data={treatments}
      setSelectedRows={selectedRowsChange}
      selectedRows={selectedRows}
    />
  );
};
